import { noop } from 'lodash';
import type { FunctionComponent, PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import { defaultKeybindingsHandlerIgnore, tinykeys } from 'tinykeys';

import { BOARD_COLUMN_CONTENT_SELECTOR } from '~/ui/core/focus/focus-regions';
import { useKeyboardShortcutsEnabled } from '~/ui/core/keyboard-shortcuts/KeyboardShortcutsContext';
import type { ShortcutSequence } from '~/ui/core/shortcut-tooltip/ShortcutTooltip';

export type JobArchitectureShortcutCommand =
    | 'create-cluster'
    | 'create-family'
    | 'create-jsa-entities'
    | 'create-level'
    | 'create-role'
    | 'open-language-menu'
    | 'open-cluster-menu'
    | 'open-family-menu'
    | 'open-item-actions'
    | 'open-page-menu'
    | 'open-role-menu'
    | 'open-setup-progress'
    | 'show-board-view'
    | 'show-table-view';

export const jobArchitectureShortcutHints: Readonly<
    Record<JobArchitectureShortcutCommand, readonly ShortcutSequence[]>
> = {
    'create-cluster': [{ keys: ['N', 'C'], separator: 'then' }],
    'create-family': [{ keys: ['N', 'F'], separator: 'then' }],
    'create-jsa-entities': [{ keys: ['N', 'I'], separator: 'then' }],
    'create-level': [{ keys: ['N', 'L'], separator: 'then' }],
    'create-role': [{ keys: ['N', 'R'], separator: 'then' }],
    'open-language-menu': [{ keys: ['O', 'L'], separator: 'then' }],
    'open-cluster-menu': [{ keys: ['Shift', '2'] }],
    'open-family-menu': [{ keys: ['Shift', '1'] }],
    'open-item-actions': [{ keys: ['M'] }, { keys: ['Shift', 'F10'] }],
    'open-page-menu': [{ keys: ['Shift', '4'] }],
    'open-role-menu': [{ keys: ['Shift', '3'] }],
    'open-setup-progress': [{ keys: ['O', 'P'], separator: 'then' }],
    'show-board-view': [{ keys: ['V', 'B'], separator: 'then' }],
    'show-table-view': [{ keys: ['V', 'T'], separator: 'then' }],
};

type ShortcutHandler = () => void;
type RegisterShortcut = (command: JobArchitectureShortcutCommand, handler: ShortcutHandler) => () => void;

const defaultRegisterShortcut: RegisterShortcut = () => noop;

const JobArchitectureKeyboardShortcutsContext = createContext<RegisterShortcut>(defaultRegisterShortcut);

const CHORD_TIMEOUT_MS = 1500;

const ownsShortcutKeys = (event: KeyboardEvent): boolean => {
    if (!(event.target instanceof HTMLElement)) {
        return false;
    }

    if (event.target.closest('[role="menu"], [role="menubar"], [role="combobox"], [role="dialog"]') !== null) {
        return true;
    }

    return (
        event.target.closest(`[role="listbox"], ${BOARD_COLUMN_CONTENT_SELECTOR}`) !== null &&
        event.target.closest('[data-item-context-menu-trigger="true"]') === null
    );
};

const ignoreKeyboardShortcut = (event: KeyboardEvent): boolean =>
    event.isComposing || ownsShortcutKeys(event) || defaultKeybindingsHandlerIgnore(event);

export const JobArchitectureKeyboardShortcutsProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
    const handlersRef = useRef<Map<JobArchitectureShortcutCommand, ShortcutHandler>>(new Map());
    const keyboardShortcutsEnabled = useKeyboardShortcutsEnabled();

    const registerShortcut = useCallback<RegisterShortcut>((command, handler) => {
        handlersRef.current.set(command, handler);

        return () => {
            if (handlersRef.current.get(command) === handler) {
                handlersRef.current.delete(command);
            }
        };
    }, []);

    useEffect(() => {
        if (!keyboardShortcutsEnabled) {
            return;
        }

        const invoke =
            (command: JobArchitectureShortcutCommand) =>
            (event: KeyboardEvent): void => {
                const handler = handlersRef.current.get(command);

                if (event.defaultPrevented || handler === undefined) {
                    return;
                }

                event.preventDefault();
                handler();
            };

        return tinykeys(
            window,
            {
                'n c': invoke('create-cluster'),
                'n f': invoke('create-family'),
                'n i': invoke('create-jsa-entities'),
                'n l': invoke('create-level'),
                'n r': invoke('create-role'),
                'o l': invoke('open-language-menu'),
                'o p': invoke('open-setup-progress'),
                m: invoke('open-item-actions'),
                'Shift+Digit1': invoke('open-family-menu'),
                'Shift+Digit2': invoke('open-cluster-menu'),
                'Shift+Digit3': invoke('open-role-menu'),
                'Shift+Digit4': invoke('open-page-menu'),
                'Shift+F10': invoke('open-item-actions'),
                'v b': invoke('show-board-view'),
                'v t': invoke('show-table-view'),
            },
            { ignore: ignoreKeyboardShortcut, timeout: CHORD_TIMEOUT_MS },
        );
    }, [keyboardShortcutsEnabled]);

    return (
        <JobArchitectureKeyboardShortcutsContext.Provider value={registerShortcut}>
            {children}
        </JobArchitectureKeyboardShortcutsContext.Provider>
    );
};

export const useJobArchitectureShortcut = (
    command: JobArchitectureShortcutCommand,
    handler: ShortcutHandler,
    enabled = true,
): void => {
    const registerShortcut = useContext(JobArchitectureKeyboardShortcutsContext);

    useEffect(() => {
        if (!enabled) {
            return;
        }

        return registerShortcut(command, handler);
    }, [command, enabled, handler, registerShortcut]);
};
